import Button from '../shared/Button.js';
import React from 'react';
import type {CSSProperties, ReactNode} from 'react';
import {AppLink, useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import {recordSeqNavOptions, labeledSeqItems} from '../lib/recordSequence.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
import {buildForecast, buildSequentialPlannedBatches, nextRealBatchName} from '../lib/cattleForecast.js';
import {unbookedForecastMessage, unbookedForecastSoon} from '../lib/cattleUnbookedForecast.js';
import type {
  CattleProcessingBatchRow,
  EnrichedScheduledBatch,
  ForecastHiddenRow,
  ForecastSettings,
  ForecastWeighInRow,
  VirtualBatch,
} from '../lib/cattleForecast.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {printRows} from '../lib/printExport.js';
import {buildProcessingBatchExportColumns} from '../lib/operationalExportColumns.js';
import {processingStatusLabel} from '../lib/processingStatusDisplay.js';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {createSavedView, updateSavedView, deleteSavedView, buildViewState} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
import {
  CATTLE_BATCH_STATUS_KEYS,
  CATTLE_BATCH_SORT_KEYS,
  buildCattleBatchPredicate,
  buildCattleBatchComparator,
} from '../lib/cattleBatchFilters.js';
import type {CattleBatchFilters, CattleBatchRow} from '../lib/cattleBatchFilters.js';
import type {CattleRecord} from '../lib/cattleHerdFilters.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
import CattleBatchPage from './CattleBatchPage.tsx';
import CattleForecastBatchPage from './CattleForecastBatchPage.tsx';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {CattleBatchCreateVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import Header from '../shared/Header.tsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import {
  computeRuminantBatchTotals,
  RuminantHangYield,
  ruminantSavedViewRadioLabelStyle,
} from './ruminantPresentation.js';
import './cattleRedesign.css';
import './cattleForecastBatchesRedesign.css';

type CattleBatchDetailRow = {
  live_weight?: number | string | null;
  hanging_weight?: number | string | null;
};
type ProcessingBatch = CattleProcessingBatchRow &
  CattleBatchRow & {
    id: string;
    name: string;
    status?: string | null;
    created_at?: string | null;
    cows_detail?: CattleBatchDetailRow[] | null;
    documents?: unknown[] | null;
  };
type CattleBatchesData = {
  batches: ProcessingBatch[];
  cattle: CattleRecord[];
  weighIns: ForecastWeighInRow[];
  calvingRecords: unknown[];
  forecastSettings: ForecastSettings | null;
  forecastIncludes: Set<string>;
  forecastHidden: ForecastHiddenRow[];
  forecastInputsReliable: boolean;
};
type PipelineBatch = ProcessingBatch | EnrichedScheduledBatch;
type EnrichedBatch = PipelineBatch & {
  animal_count: number;
  total_live_weight: number;
  total_hanging_weight: number;
  yield_pct: number | null;
};
type ToolbarPair<T extends PipelineBatch = PipelineBatch> = {raw: T; enriched: EnrichedBatch};
type PlannedRow =
  | {kind: 'scheduled'; sortKey: string; row: EnrichedScheduledBatch}
  | {kind: 'forecast'; sortKey: string; row: VirtualBatch};
type BatchSortKey = 'batchName' | 'status' | 'plannedDate' | 'animalCount' | 'yieldPct';
type BatchSortState = {key: BatchSortKey; dir: 'asc' | 'desc'};
type SavedViewRow = {
  id: string;
  name: string;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: {filters?: unknown; sortRules?: unknown} | null;
};
type CollapsibleSectionProps = {
  label: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  meta?: ReactNode;
  children?: ReactNode;
  dataKey: string;
};

const CATTLE_BATCHES_SURFACE_KEY = 'cattle.batches';
const CATTLE_BATCHES_PARAMS = Object.freeze({});
const EMPTY_CATTLE_BATCHES = Object.freeze([]) as readonly never[];
const EMPTY_CATTLE_FORECAST_INCLUDES = new Set<string>();
const EMPTY_CATTLE_FORECAST_HIDDEN = Object.freeze([]) as readonly ForecastHiddenRow[];
const EXTENDED_LIST_CONTROLS_ENABLED = false;

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as {message?: unknown}).message;
    return String(message || error);
  }
  return String(error);
}

// NOT `errorMessage` above, and the difference is the reason this exists.
//
// `errorMessage` falls back to `String(error)` when `.message` is empty, which
// is right for the load/saved-view paths that already used it. The schedule
// notice read `r.error.message` RAW, so a message-less PostgREST error rendered
// the literal text "undefined". The catalog rethrows PostgREST's `error.message`
// unchanged, so reading `.message` off the rejection reproduces that byte for
// byte — including the "undefined". Routing it through `errorMessage` would
// quietly IMPROVE the wording, and a write-path conversion may not change what
// the operator reads. Same helper, same reasoning, as the sheep pilot.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as {message?: unknown}).message) : String(err);

function cattleBatchDate(row: ProcessingBatch): string {
  return row.actual_process_date || row.planned_process_date || row.created_at || '';
}

const CATTLE_BATCH_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Planned',
  active: 'In Process',
  complete: 'Complete',
};
const CATTLE_BATCH_SORT_LABELS: Record<string, string> = {
  batchName: 'Batch name',
  status: 'Status',
  plannedDate: 'Process date',
  animalCount: 'Cow count',
  yieldPct: 'Yield %',
};

const CattleBatchesHub = () => {
  const {authState} = useAuth();
  const {setView} = useUI();
  const navigate = useNavigate();
  const {useState, useEffect, useMemo, useRef} = React;
  const role = authState && authState.role;
  const canEdit = role === 'admin' || role === 'management';

  const batchResource = useRouteResource(RESOURCE.CATTLE_BATCHES_CORE, CATTLE_BATCHES_PARAMS);
  const batchData = batchResource.data as CattleBatchesData | null;
  const batches = useMemo<ProcessingBatch[]>(
    () =>
      (batchData ? batchData.batches : EMPTY_CATTLE_BATCHES)
        .slice()
        .sort((a, b) => cattleBatchDate(b).localeCompare(cattleBatchDate(a))),
    [batchData],
  );
  const cattle = batchData ? batchData.cattle : EMPTY_CATTLE_BATCHES;
  const weighIns = batchData ? batchData.weighIns : EMPTY_CATTLE_BATCHES;
  const calvingRecs = batchData ? batchData.calvingRecords : EMPTY_CATTLE_BATCHES;
  const forecastSettings = batchData ? batchData.forecastSettings : null;
  const heiferIncludes = batchData ? batchData.forecastIncludes : EMPTY_CATTLE_FORECAST_INCLUDES;
  const hidden = batchData ? batchData.forecastHidden : EMPTY_CATTLE_FORECAST_HIDDEN;
  const loading = batchResource.loading;
  const loadError: Notice | null = batchResource.error
    ? {
        kind: 'error',
        message:
          'Could not load cattle processing batches. Please refresh the page. (' +
          errorMessage(batchResource.error) +
          ')',
      }
    : null;
  const forecastInputsReliable = batchData ? batchData.forecastInputsReliable : false;
  const loadAll = batchResource.refetch;
  // The list's one declared write. `scheduleVirtualBatch` called no refetch at
  // all — it navigated to the new record page — so the eight resources that
  // read cattle_processing_batches were connected to this insert by nothing.
  // They were not stranded (staleTime 0 refetches each on its next mount), but
  // nothing named them, so nothing could check the list was complete. The entry
  // names them and the static guard derives the required set from the read
  // catalog rather than trusting the list.
  const createBatch = useRouteMutation<CattleBatchCreateVars, Record<string, unknown> | null>(
    MUTATION.CATTLE_BATCH_CREATE,
  );
  const bookingSubmitRef = useRef(false);
  const [bookingDateDraft, setBookingDateDraft] = useState('');

  const [showCompleted, setShowCompleted] = useState<boolean>(true);
  const [scheduleDateDraft, setScheduleDateDraft] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<Notice | null>(null);

  // ── Operational-list toolbar state (search / status / range filters + a
  // single active sort rule). Persisted per-surface so a refresh keeps the
  // operator's working set. Saved views layer on top via savedViewsApi. ──
  const [filters, setFilters] = usePersistentViewState<CattleBatchFilters>('cattle.batches.filters', {});
  const [sortRule, setSortRule] = usePersistentViewState('cattle.batches.sortRule', {
    key: 'plannedDate',
    dir: 'desc',
  } as BatchSortState);
  const [selectedViewId, setSelectedViewId] = useState<string>('');
  const [showSaveViewForm, setShowSaveViewForm] = useState<boolean>(false);
  const [saveViewName, setSaveViewName] = useState<string>('');
  const [saveViewVisibility, setSaveViewVisibility] = useState<'private' | 'public'>('private');
  const [savedViewBusy, setSavedViewBusy] = useState<boolean>(false);
  const myProfileId = (authState && authState.user && authState.user.id) || null;
  const savedViewsQuery = useSavedViewsData(CATTLE_BATCHES_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;

  useEffect(() => {
    if (batchData && !forecastInputsReliable) {
      setNotice(
        (prev) =>
          prev || {
            kind: 'warning',
            message: 'Forecast data could not fully load. Planned batches may be unavailable until refresh.',
          },
      );
    }
  }, [batchData, forecastInputsReliable]);

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  const forecast = useMemo(() => {
    if (!forecastSettings) return null;
    const realBatchesOnly = batches.filter((b) => b.status === 'active' || b.status === 'complete');
    const scheduledBatchesOnly = batches.filter((b) => b.status === 'scheduled');
    return buildForecast({
      cattle,
      weighIns,
      settings: forecastSettings,
      includes: heiferIncludes,
      hidden,
      realBatches: realBatchesOnly,
      scheduledBatches: scheduledBatchesOnly,
      todayMs: Date.now(),
    });
  }, [cattle, weighIns, forecastSettings, heiferIncludes, hidden, batches]);

  const virtualPlanned = useMemo(() => {
    if (!forecast) return [];
    const nowYm = new Date().toISOString().slice(0, 7);
    const limitMs = Date.now() + 365 * 86400000;
    const limitYm = new Date(limitMs).toISOString().slice(0, 7);
    return forecast.virtualBatches.filter((vb) => vb.monthKey >= nowYm && vb.monthKey <= limitYm);
  }, [forecast]);

  // Q14-A: a forecast month within 8 weeks with no booking warns here, beside
  // the Schedule controls (Ronnie, 2026-10-09: Cattle Batches only).
  const unbookedSoon = useMemo(
    () => unbookedForecastMessage(unbookedForecastSoon(virtualPlanned, Date.now())),
    [virtualPlanned],
  );

  const scheduledList = useMemo(() => {
    return (
      forecast?.scheduledBatches ||
      buildSequentialPlannedBatches({
        scheduledBatches: batches.filter((batch) => batch.status === 'scheduled'),
      }).scheduledBatches
    );
  }, [forecast, batches]);

  async function scheduleVirtualBatch(vb: VirtualBatch) {
    const date = (scheduleDateDraft[vb.name] || '').trim();
    await scheduleProcessingDate(date, date.slice(0, 7) === vb.monthKey ? vb.name : undefined);
  }

  async function scheduleProcessingDate(date: string, suggestedName?: string) {
    if (!canEdit || bookingSubmitRef.current) return;
    setNotice(null);
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setNotice({kind: 'error', message: 'Pick a processor date (YYYY-MM-DD) before scheduling.'});
      return;
    }
    const name =
      suggestedName && !batches.some((batch) => batch.name === suggestedName)
        ? suggestedName
        : nextRealBatchName(batches, date);
    if (!name) {
      setNotice({kind: 'error', message: 'Pick a valid processor date before scheduling.'});
      return;
    }
    bookingSubmitRef.current = true;
    const rowId = 'cpb-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
    // Migration 240: the create is the audited batch.create operation — the
    // row is server-built from (id, name, planned date) exactly as the old
    // literal built it, status 'scheduled', and the record.created Activity
    // row ('Scheduled <name> for <date>') is server-written in the same
    // transaction. The notice and the navigation stay here.
    try {
      await createBatch.run({batchId: rowId, name, plannedProcessDate: date});
    } catch (err) {
      bookingSubmitRef.current = false;
      setNotice({kind: 'error', message: 'Schedule failed: ' + writeErrorMessage(err)});
      return;
    }
    bookingSubmitRef.current = false;
    navigate('/cattle/batches/' + rowId);
  }

  const active = batches.filter((b) => b.status === 'active');
  const completed = batches.filter((b) => b.status === 'complete');
  const completedCowCount = completed.reduce(
    (sum, batch) => sum + (Array.isArray(batch.cows_detail) ? batch.cows_detail.length : 0),
    0,
  );

  // Enrich a pipeline batch into the export/filter shape the rest of the view
  // (and the shared processing-batch export columns) read. Scheduled rows are
  // forecast-backed (animalIds, no cows_detail); active/processed rows carry a
  // cows_detail array. animal_count + yield_pct are the fields the toolbar
  // filters/sorts on, so deriving them here keeps filter + sort + CSV/print on
  // one shape.
  function enrichBatch(batch: PipelineBatch): EnrichedBatch {
    const detailRows: CattleBatchDetailRow[] = Array.isArray(batch.cows_detail) ? batch.cows_detail : [];
    const {totalLive, totalHang, rawYieldPct} = computeRuminantBatchTotals(detailRows);
    return {
      ...batch,
      // Operational exports + the count filter/sort count ATTACHED detail rows
      // only (Lane K contract, parity with sheep). Scheduled/forecast batches
      // still display "N cows forecast" separately via batch.animalIds.
      animal_count: detailRows.length,
      total_live_weight: totalLive,
      total_hanging_weight: totalHang,
      yield_pct: rawYieldPct,
    };
  }

  // One predicate + comparator drive filtering/sorting across every pipeline
  // section (scheduled / active / processed). Filtering runs on the enriched
  // shape; we keep each section's ORIGINAL rows aligned so render + nav still
  // pass the real batch objects through. Single active sort rule (right-sized).
  const effectiveFilters = EXTENDED_LIST_CONTROLS_ENABLED ? filters : {};
  const effectiveSortRule = EXTENDED_LIST_CONTROLS_ENABLED ? sortRule : {key: 'plannedDate', dir: 'desc'};
  const batchPredicate = buildCattleBatchPredicate(effectiveFilters);
  const batchComparator = buildCattleBatchComparator(effectiveSortRule);
  function applyToolbar<T extends PipelineBatch>(rows: T[]): ToolbarPair<T>[] {
    return rows
      .map((b) => ({raw: b, enriched: enrichBatch(b)}))
      .filter((pair) => batchPredicate(pair.enriched))
      .sort((a, b) => batchComparator(a.enriched, b.enriched));
  }
  const scheduledPairs = applyToolbar(scheduledList);
  const activePairs = applyToolbar(active);
  const completedPairs = applyToolbar(completed);
  const scheduledVisible = scheduledPairs.map((p) => p.raw);
  const activeVisible = activePairs.map((p) => p.raw);
  const completedVisible = completedPairs.map((p) => p.raw);
  const completedYields = completed
    .map((batch) => enrichBatch(batch).yield_pct)
    .filter((value): value is number => value != null && Number.isFinite(value));
  const averageYield =
    completedYields.length > 0 ? completedYields.reduce((sum, value) => sum + value, 0) / completedYields.length : null;

  // Consolidated Planned pipeline: persisted scheduled rows (toolbar-filtered
  // like every persisted section) interleaved with virtual forecast rows
  // (never toolbar-filtered — they aren't persisted records) in chronological
  // order. Scheduled rows sort by their exact processor date; forecast rows
  // anchor to the month's 15th (the forecast checkpoint date).
  const plannedRows = [
    ...scheduledVisible.map((row) => ({
      kind: 'scheduled' as const,
      sortKey: row.planned_process_date || (row.monthKey || '') + '-15',
      row,
    })),
    ...virtualPlanned.map((row) => ({kind: 'forecast' as const, sortKey: row.monthKey + '-15', row})),
  ].sort((a, b) => a.sortKey.localeCompare(b.sortKey)) as PlannedRow[];
  const plannedHiddenByFilters = scheduledList.length - scheduledVisible.length;

  // Visible/rendered order for record sequence nav (scheduled → active → then
  // complete ONLY when the Show Complete Batches section is expanded).
  // Virtual/planned forecast tiles are excluded — they don't route. This is the
  // filtered + sorted set, so nav stepping matches what the operator sees.
  const batchSeqRows = [...scheduledVisible, ...activeVisible, ...(showCompleted ? completedVisible : [])];
  // CSV/print is fed the SAME filtered + sorted set (enriched), never the raw
  // batches list.
  const batchExportRows = [...scheduledPairs, ...activePairs, ...(showCompleted ? completedPairs : [])].map(
    (p) => p.enriched,
  );

  // Routable total (pre-filter) vs visible (post-filter) for the "N of M"
  // count + the filtered-no-results empty state. Completed only counts toward
  // the total when its section is expanded (same rule as the rendered set).
  const routableTotal = scheduledList.length + active.length + (showCompleted ? completed.length : 0);
  const visibleTotal = batchSeqRows.length;
  const filtersActive = Object.keys(effectiveFilters || {}).length > 0;

  const exportColumns = buildProcessingBatchExportColumns({fmt, animalLabel: 'Cow'});

  function handleExportCsv() {
    if (!batchExportRows.length) {
      setNotice({kind: 'warning', message: 'No visible cattle processing batches to export.'});
      return;
    }
    const ok = downloadCsv(csvFilename('cattle-processing-batches'), rowsToCsv(exportColumns, batchExportRows));
    if (!ok) setNotice({kind: 'warning', message: 'CSV export is unavailable in this browser.'});
  }

  function handlePrintRows() {
    if (!batchExportRows.length) {
      setNotice({kind: 'warning', message: 'No visible cattle processing batches to print.'});
      return;
    }
    const ok = printRows({
      title: 'Cattle Processing Batches',
      subtitle: batchExportRows.length + ' visible batches',
      columns: exportColumns,
      rows: batchExportRows,
    });
    if (!ok) setNotice({kind: 'warning', message: 'Print export is unavailable in this browser.'});
  }

  // ── Toolbar helpers (filter mutation + saved-view CRUD) ──────────────────
  function setFilter<K extends keyof CattleBatchFilters>(key: K, value: CattleBatchFilters[K]) {
    setFilters((prev) => {
      const next = {...prev};
      if (
        value == null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
      ) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setSelectedViewId('');
  }
  function setRangeBound(key: 'animalCountRange', bound: 'min' | 'max', raw: string) {
    setFilters((prev) => {
      const cur = prev[key] && typeof prev[key] === 'object' ? {...prev[key]} : {};
      if (raw === '' || raw == null) {
        delete cur[bound];
      } else {
        const num = parseInt(raw, 10);
        if (!Number.isFinite(num)) delete cur[bound];
        else cur[bound] = num;
      }
      const next = {...prev};
      if (Object.keys(cur).length === 0) delete next[key];
      else next[key] = cur;
      return next;
    });
    setSelectedViewId('');
  }
  function setDateBound(key: 'plannedDateRange', bound: 'after' | 'before', raw: string) {
    setFilters((prev) => {
      const cur = prev[key] && typeof prev[key] === 'object' ? {...prev[key]} : {};
      if (raw === '' || raw == null) delete cur[bound];
      else cur[bound] = raw;
      const next = {...prev};
      if (Object.keys(cur).length === 0) delete next[key];
      else next[key] = cur;
      return next;
    });
    setSelectedViewId('');
  }
  function toggleStatus(value: string) {
    setFilters((prev) => {
      const curList = Array.isArray(prev.status) ? prev.status : [];
      const nextList = curList.includes(value) ? curList.filter((x) => x !== value) : [...curList, value];
      const next = {...prev};
      if (nextList.length === 0) delete next.status;
      else next.status = nextList;
      return next;
    });
    setSelectedViewId('');
  }
  function clearAllFilters() {
    setFilters({});
    setSelectedViewId('');
  }
  function setSortKey(key: string) {
    setSortRule((prev) => ({key: key as BatchSortKey, dir: (prev && prev.dir) || 'asc'}));
    setSelectedViewId('');
  }
  function flipSortDir() {
    setSortRule((prev) => ({
      key: (prev && prev.key) || 'plannedDate',
      dir: prev && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
    setSelectedViewId('');
  }

  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );

  function cattleBatchesViewState() {
    return buildViewState({filters, sortRules: [sortRule], viewMode: 'grouped'});
  }
  function applySavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    const st = view.view_state || {};
    setFilters(st.filters && typeof st.filters === 'object' ? (st.filters as CattleBatchFilters) : {});
    const rules = (Array.isArray(st.sortRules) ? st.sortRules : []) as BatchSortState[];
    setSortRule(rules[0] && rules[0].key ? rules[0] : {key: 'plannedDate', dir: 'desc'});
  }
  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    if (!id) return;
    applySavedView(savedViews.find((v) => v.id === id));
  }
  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setShowSaveViewForm(true);
  }
  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: CATTLE_BATCHES_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: cattleBatchesViewState(),
      })) as SavedViewRow | null;
      setShowSaveViewForm(false);
      await loadSavedViews();
      if (created && created.id) setSelectedViewId(created.id);
    } catch (e) {
      setNotice({kind: 'error', message: 'Save view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }
  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: cattleBatchesViewState()});
      await loadSavedViews();
      setNotice({kind: 'success', message: 'Updated "' + selectedView.name + '" to the current filters/sort.'});
    } catch (e) {
      setNotice({kind: 'error', message: 'Update view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }
  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      setNotice({kind: 'error', message: 'Delete view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }
  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    const view = selectedView;
    const run = () => {
      void proceedDeleteSelectedView(view);
    };
    if (window._wcfConfirmDelete) {
      window._wcfConfirmDelete('Delete saved view "' + view.name + '"?', run);
    } else {
      run();
    }
  }

  const toolbarInputS: CSSProperties = {
    fontSize: 12,
    padding: '6px 10px',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const savedViewRadioLabelS = ruminantSavedViewRadioLabelStyle;

  return (
    <div
      className="cattle-redesign cattle-batches"
      data-cattle-batches-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      <div className="cattle-page">
        <InlineNotice notice={loadError} />
        {loadError && (
          <Button variant="secondary" type="button" onClick={loadAll} style={{marginBottom: 12}}>
            Retry
          </Button>
        )}
        <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
        <div className="cattle-batches-title-row">
          <div data-cattle-batches-root>
            <h1 className="cattle-page-title">Processing Batches</h1>
            <p className="cattle-page-summary">
              <strong>{scheduledList.length}</strong> booked · <strong>{virtualPlanned.length}</strong> forecast ·{' '}
              <strong>{active.length}</strong> in process · <strong>{completed.length}</strong> complete
              {' · '}
              <span title="Mean hang yield across complete batches with recorded live and hanging weights">
                avg yield <strong>{averageYield == null ? '—' : averageYield.toFixed(1) + '%'}</strong>
              </span>
            </p>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
            {EXTENDED_LIST_CONTROLS_ENABLED && (
              <>
                <Button variant="secondary" type="button" onClick={handleExportCsv} data-cattle-batches-export-csv="1">
                  Export CSV
                </Button>
                <Button variant="secondary" type="button" onClick={handlePrintRows} data-cattle-batches-print="1">
                  Print
                </Button>
              </>
            )}
            {!canEdit && <Badge variant="info">READ-ONLY</Badge>}
          </div>
        </div>

        {loading && <div style={{textAlign: 'center', padding: '2rem', color: 'var(--ink-faint)'}}>Loading{'…'}</div>}

        {/* Saved views row — degrades to a small notice if it can't load,
            never blocking the list/filters below. */}
        {EXTENDED_LIST_CONTROLS_ENABLED && !loading && !loadError && (
          <div
            data-cattle-batches-saved-views-row
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
            {savedViewsError ? (
              <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-cattle-batches-saved-views-error>
                Saved views unavailable. Filters still work.
              </span>
            ) : (
              <>
                <select
                  data-cattle-batches-saved-view-select
                  value={selectedViewId}
                  disabled={savedViewsLoading}
                  onChange={(e) => onSelectSavedView(e.target.value)}
                  style={{...toolbarInputS, width: 'auto', minWidth: 200}}
                >
                  <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                  {myViews.length > 0 && (
                    <optgroup label="My views">
                      {myViews.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {publicOtherViews.length > 0 && (
                    <optgroup label="Public views">
                      {publicOtherViews.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {selectedViewIsMine && (
                  <>
                    <Button
                      variant="secondary"
                      type="button"
                      data-cattle-batches-saved-view-update
                      onClick={updateSelectedView}
                      disabled={savedViewBusy}
                    >
                      Update to current
                    </Button>
                    <Button
                      variant="danger"
                      type="button"
                      data-cattle-batches-saved-view-delete
                      onClick={deleteSelectedView}
                      disabled={savedViewBusy}
                    >
                      Delete
                    </Button>
                  </>
                )}
                <span style={{flex: 1}} />
                <Button
                  variant="secondary"
                  type="button"
                  data-cattle-batches-saved-view-save-open
                  onClick={openSaveViewForm}
                  disabled={savedViewBusy}
                >
                  Save current view
                </Button>
              </>
            )}
          </div>
        )}
        {EXTENDED_LIST_CONTROLS_ENABLED && !loading && !loadError && showSaveViewForm && (
          <div
            data-cattle-batches-saved-view-form
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <input
              data-cattle-batches-saved-view-name
              type="text"
              value={saveViewName}
              placeholder="View name"
              onChange={(e) => setSaveViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitSaveView();
              }}
              style={{...toolbarInputS, flex: 1, minWidth: 200}}
            />
            <label style={savedViewRadioLabelS}>
              <input
                type="radio"
                name="saveCattleBatchViewVisibility"
                checked={saveViewVisibility === 'private'}
                onChange={() => setSaveViewVisibility('private')}
                data-cattle-batches-saved-view-visibility="private"
              />
              Private
            </label>
            <label style={savedViewRadioLabelS}>
              <input
                type="radio"
                name="saveCattleBatchViewVisibility"
                checked={saveViewVisibility === 'public'}
                onChange={() => setSaveViewVisibility('public')}
                data-cattle-batches-saved-view-visibility="public"
              />
              Public
            </label>
            <Button
              variant="secondary"
              type="button"
              data-cattle-batches-saved-view-save
              onClick={submitSaveView}
              disabled={savedViewBusy}
            >
              Save
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowSaveViewForm(false)}
              disabled={savedViewBusy}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Operational toolbar — search + status + planned-date range + cow
            count range + sort + direction, filtering/sorting across every
            pipeline section at once. Right-sized: a single flat row, not the
            multi-group chip popovers of the herds tab. */}
        {EXTENDED_LIST_CONTROLS_ENABLED && !loading && !loadError && (
          <div
            data-cattle-batches-toolbar
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '12px 16px',
              marginBottom: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
              <input
                type="text"
                data-cattle-batches-search
                value={(filters && filters.textSearch) || ''}
                onChange={(e) => setFilter('textSearch', e.target.value)}
                placeholder="Search batch name, notes..."
                style={{...toolbarInputS, flex: 1, minWidth: 200}}
              />
              <div
                style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}
                data-cattle-batches-status-filter
              >
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Status</span>
                {CATTLE_BATCH_STATUS_KEYS.map((s) => {
                  const statusFilters = filters && filters.status;
                  const on = Array.isArray(statusFilters) && statusFilters.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      data-cattle-batches-status-option={s}
                      onClick={() => toggleStatus(s)}
                      style={{
                        fontSize: 12,
                        padding: '5px 10px',
                        borderRadius: 10,
                        border: '1px solid ' + (on ? 'var(--primary)' : 'var(--border-strong)'),
                        background: 'var(--surface)',
                        color: on ? 'var(--primary)' : 'var(--ink-muted)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {CATTLE_BATCH_STATUS_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 6}} data-cattle-batches-planned-range>
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Process date</span>
                <input
                  type="date"
                  data-cattle-batches-planned-after
                  value={(filters && filters.plannedDateRange && filters.plannedDateRange.after) || ''}
                  onChange={(e) => setDateBound('plannedDateRange', 'after', e.target.value)}
                  style={{...toolbarInputS, width: 'auto'}}
                />
                <span style={{fontSize: 11, color: 'var(--ink-faint)'}}>to</span>
                <input
                  type="date"
                  data-cattle-batches-planned-before
                  value={(filters && filters.plannedDateRange && filters.plannedDateRange.before) || ''}
                  onChange={(e) => setDateBound('plannedDateRange', 'before', e.target.value)}
                  style={{...toolbarInputS, width: 'auto'}}
                />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 6}} data-cattle-batches-count-range>
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Cows</span>
                <input
                  type="number"
                  min="0"
                  data-cattle-batches-count-min
                  value={(filters && filters.animalCountRange && filters.animalCountRange.min) ?? ''}
                  onChange={(e) => setRangeBound('animalCountRange', 'min', e.target.value)}
                  placeholder="min"
                  style={{...toolbarInputS, width: 72}}
                />
                <span style={{fontSize: 11, color: 'var(--ink-faint)'}}>to</span>
                <input
                  type="number"
                  min="0"
                  data-cattle-batches-count-max
                  value={(filters && filters.animalCountRange && filters.animalCountRange.max) ?? ''}
                  onChange={(e) => setRangeBound('animalCountRange', 'max', e.target.value)}
                  placeholder="max"
                  style={{...toolbarInputS, width: 72}}
                />
              </div>
              <span style={{flex: 1}} />
              <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Sort</span>
                <select
                  data-cattle-batches-sort-key
                  value={(sortRule && sortRule.key) || 'plannedDate'}
                  onChange={(e) => setSortKey(e.target.value)}
                  style={{...toolbarInputS, width: 'auto'}}
                >
                  {CATTLE_BATCH_SORT_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {CATTLE_BATCH_SORT_LABELS[k]}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  type="button"
                  data-cattle-batches-sort-dir
                  onClick={flipSortDir}
                  title={sortRule && sortRule.dir === 'desc' ? 'Descending' : 'Ascending'}
                >
                  {sortRule && sortRule.dir === 'desc' ? 'Desc ↓' : 'Asc ↑'}
                </Button>
              </div>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
              <span style={{fontSize: 12, color: 'var(--ink-muted)'}} data-cattle-batches-count>
                {visibleTotal} of {routableTotal} {routableTotal === 1 ? 'batch' : 'batches'}
              </span>
              {filtersActive && (
                <Button variant="secondary" type="button" data-cattle-batches-clear-filters onClick={clearAllFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Keep the chronological pipeline and existing detail destinations; only the batch links are clickable. */}
        {!loading && !loadError && (
          <section className="cattle-card" data-planned-section>
            <div className="cattle-section-header cattle-batch-section-heading">
              {/* T11/Q05: only a program or group dot may precede a heading. The
                  status-coloured dots these three sections carried are gone; the
                  heading and its count name the section. */}
              <h2>Planned</h2>
              <span className="cattle-count">
                {plannedRows.length}
                {plannedHiddenByFilters > 0 ? ' of ' + (plannedRows.length + plannedHiddenByFilters) : ''}
              </span>
              {canEdit && (
                <div className="cattle-batch-booking">
                  <span>Book a processor date now, choose cattle later</span>
                  <input
                    id="cattle-booking-date"
                    type="date"
                    aria-label="Processor date for a new booking"
                    value={bookingDateDraft}
                    onChange={(event) => setBookingDateDraft(event.target.value)}
                    disabled={createBatch.pending}
                  />
                  <Button
                    variant="primary"
                    type="button"
                    className="cattle-button cattle-button-primary"
                    data-cattle-schedule-processing
                    onClick={() => scheduleProcessingDate(bookingDateDraft)}
                    disabled={!bookingDateDraft || createBatch.pending}
                  >
                    {createBatch.pending ? 'Scheduling...' : 'Schedule processing'}
                  </Button>
                </div>
              )}
            </div>
            {unbookedSoon ? (
              <div data-cattle-unbooked-forecast>
                <InlineNotice notice={{kind: 'warning', message: unbookedSoon}} />
              </div>
            ) : null}
            {plannedRows.length === 0 ? (
              <div data-cattle-batches-planned-empty className="cattle-batch-empty">
                {plannedHiddenByFilters > 0
                  ? 'No planned batches match the current filters.'
                  : 'No planned batches in the next 12 months — the forecast has no eligible cattle landing in the display window.'}
              </div>
            ) : (
              <div className="cattle-table-scroll">
                <div className="cattle-batch-planned-table">
                  <div className="cattle-batch-planned-grid cattle-batch-table-head">
                    <span>Batch</span>
                    <span>Status</span>
                    <span>Month</span>
                    <span className="cattle-batch-number">Cows</span>
                    <span className="cattle-batch-number">Projected</span>
                    <span>Processor date</span>
                  </div>
                  {plannedRows.map((entry) =>
                    entry.kind === 'scheduled' ? (
                      <div
                        key={entry.row.id}
                        data-scheduled-batch={entry.row.name}
                        data-batch-row={entry.row.id}
                        data-planned-row="scheduled"
                        className="cattle-batch-planned-grid cattle-batch-planned-row"
                      >
                        <AppLink
                          to={'/cattle/batches/' + entry.row.id}
                          state={recordSeqNavOptions(labeledSeqItems(batchSeqRows, 'name')).state}
                          className="cattle-batch-link"
                          aria-label={'Open scheduled batch ' + entry.row.name}
                        >
                          {entry.row.name}
                        </AppLink>
                        <span>
                          <Badge status="scheduled" />
                        </span>
                        <span className="cattle-cell-primary">{entry.row.label || '—'}</span>
                        <span className="cattle-batch-two-line cattle-batch-number">
                          <span>{entry.row.animalIds.length}</span>
                          <span className="cattle-cell-secondary">forecast</span>
                        </span>
                        <strong className="cattle-batch-number">
                          {Math.round(entry.row.projectedTotalLbs || 0).toLocaleString()} lb
                        </strong>
                        <span className="cattle-batch-two-line">
                          <span className="cattle-cell-primary">
                            {entry.row.planned_process_date ? fmt(entry.row.planned_process_date) : '—'}
                          </span>
                          <span className="cattle-cell-secondary">
                            Booked · cattle not assigned yet (choose in Weigh-Ins)
                          </span>
                        </span>
                      </div>
                    ) : (
                      <div
                        key={entry.row.name}
                        data-virtual-batch={entry.row.name}
                        data-virtual-batch-month={entry.row.monthKey}
                        data-planned-row="forecast"
                        className="cattle-batch-planned-grid cattle-batch-planned-row"
                      >
                        <AppLink
                          to={'/cattle/batches/forecast/' + entry.row.monthKey}
                          className="cattle-batch-link"
                          aria-label={'Open forecast batch ' + entry.row.name}
                        >
                          {entry.row.name}
                        </AppLink>
                        <span>
                          <Badge variant="neutral">Forecast</Badge>
                        </span>
                        {/* A forecast month is an estimate, not a booking (Q14-A). */}
                        <span className="cattle-cell-secondary" data-forecast-month-estimate>
                          {entry.row.label} est.
                        </span>
                        <span className="cattle-batch-two-line cattle-batch-number">
                          <span>{entry.row.animalIds.length}</span>
                          <span className="cattle-cell-secondary">forecast</span>
                        </span>
                        <strong className="cattle-batch-number">
                          {Math.round(entry.row.projectedTotalLbs || 0).toLocaleString()} lb
                        </strong>
                        {canEdit ? (
                          <span className="cattle-batch-row-schedule" onClick={(event) => event.stopPropagation()}>
                            <input
                              data-virtual-batch-schedule-date={entry.row.name}
                              type="date"
                              aria-label={'Processor date for ' + entry.row.name}
                              value={scheduleDateDraft[entry.row.name] || ''}
                              onChange={(event) =>
                                setScheduleDateDraft((previous) => ({
                                  ...previous,
                                  [entry.row.name]: event.target.value,
                                }))
                              }
                              disabled={createBatch.pending}
                            />
                            <Button
                              variant="secondary"
                              type="button"
                              className="cattle-button"
                              data-virtual-batch-schedule={entry.row.name}
                              onClick={(event) => {
                                event.stopPropagation();
                                scheduleVirtualBatch(entry.row);
                              }}
                              disabled={!scheduleDateDraft[entry.row.name] || createBatch.pending}
                            >
                              Schedule
                            </Button>
                          </span>
                        ) : (
                          <span className="cattle-cell-secondary">Created when sent to processor at Weigh-Ins</span>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {!loading && !loadError && (
          <section className="cattle-card" data-batches-section="active">
            <div className="cattle-section-header cattle-batch-section-heading">
              <h2>In Process</h2>
              <span className="cattle-count">
                {activeVisible.length}
                {activeVisible.length !== active.length ? ' of ' + active.length : ''}
              </span>
            </div>
            {active.length === 0 ? (
              <div className="cattle-batch-empty">
                No batches in process. Cattle join a batch when they're flagged <strong>Send to processor</strong>{' '}
                during a Finishers weigh-in.
              </div>
            ) : activeVisible.length === 0 ? (
              <div data-cattle-batches-active-empty-filtered className="cattle-batch-empty">
                No in-process batches match the current filters.
              </div>
            ) : (
              <div className="cattle-table-scroll">
                <div className="cattle-batch-complete-table">
                  <div className="cattle-batch-complete-grid cattle-batch-table-head">
                    <span>Batch</span>
                    <span>Status</span>
                    <span>Process date</span>
                    <span className="cattle-batch-number">Cows</span>
                    <span>Hang yield</span>
                  </div>
                  {activeVisible.map((batch) => {
                    const rows: CattleBatchDetailRow[] = Array.isArray(batch.cows_detail) ? batch.cows_detail : [];
                    const {yieldPct} = computeRuminantBatchTotals(rows);
                    return (
                      <div
                        key={batch.id}
                        data-batch-row={batch.id}
                        data-batch-name={batch.name}
                        data-batch-status={batch.status}
                        className="cattle-batch-complete-grid cattle-batch-record-row wcf-clickable"
                        {...openableProps(() =>
                          navigate(
                            '/cattle/batches/' + batch.id,
                            recordSeqNavOptions(labeledSeqItems(batchSeqRows, 'name')),
                          ),
                        )}
                        aria-label={'Open batch ' + batch.name}
                      >
                        <strong>{batch.name}</strong>
                        <span>
                          <Badge status={processingStatusLabel(batch.status)} />
                        </span>
                        <span className="cattle-cell-primary">
                          {batch.actual_process_date ? fmt(batch.actual_process_date) : '—'}
                        </span>
                        <span className="cattle-batch-number">{rows.length}</span>
                        <RuminantHangYield value={yieldPct} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {!loading && !loadError && (
          <CollapsibleSection
            label="Complete"
            count={completed.length}
            expanded={showCompleted}
            onToggle={() => setShowCompleted((value) => !value)}
            dataKey="processed"
            meta={
              <span>
                {completedCowCount} {completedCowCount === 1 ? 'cow' : 'cows'} processed
              </span>
            }
          >
            {completed.length === 0 ? (
              <div className="cattle-batch-empty">No complete batches yet.</div>
            ) : completedVisible.length === 0 ? (
              <div data-cattle-batches-processed-empty-filtered className="cattle-batch-empty">
                No complete batches match the current filters.
              </div>
            ) : (
              <div className="cattle-table-scroll">
                <div className="cattle-batch-complete-table">
                  <div className="cattle-batch-complete-grid cattle-batch-table-head">
                    <span>Batch</span>
                    <span>Status</span>
                    <span>Processed</span>
                    <span className="cattle-batch-number">Cows</span>
                    <span>Hang yield</span>
                  </div>
                  {completedVisible.map((batch) => {
                    const rows: CattleBatchDetailRow[] = Array.isArray(batch.cows_detail) ? batch.cows_detail : [];
                    const {yieldPct} = computeRuminantBatchTotals(rows);
                    return (
                      <div
                        key={batch.id}
                        data-batch-row={batch.id}
                        data-batch-name={batch.name}
                        data-batch-status={batch.status}
                        className="cattle-batch-complete-grid cattle-batch-record-row wcf-clickable"
                        {...openableProps(() =>
                          navigate(
                            '/cattle/batches/' + batch.id,
                            recordSeqNavOptions(labeledSeqItems(batchSeqRows, 'name')),
                          ),
                        )}
                        aria-label={'Open complete batch ' + batch.name}
                      >
                        <strong>{batch.name}</strong>
                        <span>
                          <Badge status={processingStatusLabel(batch.status)} />
                        </span>
                        <span className="cattle-cell-primary">
                          {batch.actual_process_date || batch.planned_process_date
                            ? fmt(batch.actual_process_date || batch.planned_process_date)
                            : '—'}
                        </span>
                        <span className="cattle-batch-number">{rows.length}</span>
                        <RuminantHangYield value={yieldPct} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};

function CollapsibleSection({label, count, expanded, onToggle, meta, children, dataKey}: CollapsibleSectionProps) {
  const contentsId = 'cattle-batches-section-' + dataKey;
  return (
    <section className="cattle-card" data-batches-section={dataKey}>
      <button
        type="button"
        className="cattle-section-header cattle-batch-section-heading cattle-batch-collapse wcf-clickable"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={contentsId}
      >
        <span className={'cattle-forecast-chevron' + (expanded ? ' is-open' : '')} aria-hidden="true">
          ›
        </span>
        <span className="cattle-batch-section-title">{label}</span>
        <span className="cattle-count">{count}</span>
        {meta && <span className="cattle-batch-section-meta">{meta}</span>}
      </button>
      {expanded && <div id={contentsId}>{children}</div>}
    </section>
  );
}
function CattleBatchesRouter(props: Record<string, never>) {
  const {authState} = useAuth();
  const location = useLocation();
  // Forecast-only planned batch detail: a live projection keyed by the
  // STABLE monthKey — not a persisted cattle_processing_batches record. The
  // distinct /forecast/ segment keeps it from ever masquerading as (or
  // clobbering) a /cattle/batches/:id record id.
  if (location.pathname.startsWith('/cattle/batches/forecast/')) {
    const monthKey = location.pathname.slice('/cattle/batches/forecast/'.length) || null;
    return React.createElement(CattleForecastBatchPage, {
      sb,
      fmt,
      authState,
      monthKey,
    });
  }
  const batchDetailId = location.pathname.startsWith('/cattle/batches/')
    ? location.pathname.slice('/cattle/batches/'.length) || null
    : null;
  if (batchDetailId) {
    return React.createElement(CattleBatchPage, {
      sb,
      fmt,
      authState,
    });
  }
  return React.createElement(CattleBatchesHub, props);
}

export default CattleBatchesRouter;
